import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2 } from "lucide-react";
import { LogPanel } from "features/cv-processor/ui/LogPanel";
import type { LogEvent, TokenUsage } from "features/cv-processor/ui/LogPanel";
import { fetchAlertEnhancementLogs } from "../api/LinkedInJobsApi";

interface Props {
  alertId: number;
  isAdmin: boolean;
  onClose: () => void;
}

export default function EnhancementLogsModal({ alertId, isAdmin, onClose }: Props) {
  const [logs, setLogs] = useState<LogEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAlertEnhancementLogs(alertId, isAdmin)
      .then((data) => { if (!cancelled) setLogs(data as LogEvent[]); })
      .catch(() => { if (!cancelled) setError("Failed to load enhancement logs."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [alertId, isAdmin]);

  const tokenUsage: TokenUsage | null = (() => {
    const tu = logs.find((e) => e.type === "token_usage");
    if (!tu) return null;
    return {
      prompt_tokens: tu.prompt_tokens ?? 0,
      completion_tokens: tu.completion_tokens ?? 0,
      total_tokens: tu.total_tokens ?? 0,
      cache_hit_tokens: tu.cache_hit_tokens ?? 0,
    };
  })();

  const provider: "openai" | "deepseek" | null = (() => {
    const llm = logs.find((e) => e.type === "llm_input" && e.model);
    if (!llm?.model) return null;
    return llm.model.includes("deepseek") ? "deepseek" : "openai";
  })();

  return createPortal(
    <>
      <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="fixed z-[71] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-surface border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
            <h2 className="text-sm font-semibold text-foreground font-inter">Enhancement Logs</h2>
            <button
              onClick={onClose}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground font-inter text-sm gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading logs…
              </div>
            ) : error ? (
              <p className="text-sm text-red-400 font-inter text-center py-8">{error}</p>
            ) : logs.length === 0 ? (
              <p className="text-sm text-muted-foreground font-inter text-center py-8 italic">
                No logs stored for this enhancement.
              </p>
            ) : (
              <LogPanel
                logs={logs}
                logsEndRef={logsEndRef}
                enhancing={false}
                provider={provider}
                tokenUsage={tokenUsage}
              />
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
